import * as React from "react";
import supabase from "@clients/supabase";
import {
  CourseClosure,
  CourseWarning,
  PublicHoliday,
  CourseRate,
  mapWarningStateToText,
  toDayOfWeek,
  pluralize,
  getLastContractEndDate,
  mapClosureStateToText,
  getActiveWarnings,
  getActivePublicHolidays,
  Days,
  getActiveClosures,
  CourseRate_Response,
  cloneObject,
  getCourseRatesForDate,
  GolfRate,
} from "@kalabo/golfsavers-common";

import dayjs, { Dayjs } from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
dayjs.extend(customParseFormat); // try this.

import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
dayjs.extend(isSameOrBefore);

import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
dayjs.extend(isSameOrAfter);

import utc from "dayjs/plugin/utc";
dayjs.extend(utc);
import timezone from "dayjs/plugin/timezone";
dayjs.extend(timezone);

import _ from "lodash";
import { useApp } from "@store/app";
import { RateItem } from "../rateItem";
import { Alert, Text } from "@uikit";
import { RateWarning } from "@components/course/rateSearch/rateWarning";
import RateEnquiry from "@components/course/rateSearch/rateEnquiry";
import { useCheckout } from "@store/checkout";

export interface RateBlockProps {
  agent: any;
  onSelected: (rate: CourseRate) => void;
  onModeChange?: (mode: any) => void;
  display_currency?: any;
  contracts: any;
  private_club: boolean;
  closures: CourseClosure[];
  warnings: CourseWarning[];
  public_holidays?: PublicHoliday[];
  course_id: string;
  number_of_golfers: number;
  date: string;
  weekend_days?: Days;
}

export const RateBlock = ({
  agent,
  onSelected,
  onModeChange,
  display_currency,
  contracts,
  closures,
  private_club,
  warnings,
  public_holidays,
  course_id,
  number_of_golfers,
  date,
  weekend_days,
}: RateBlockProps) => {
  const [loading, setLoading] = React.useState(true);
  const [mode, setMode] = React.useState("rates"); // rates, enquiry
  const cart = useCheckout(($) => $.cart);
  const [rates, setRates] = React.useState<GolfRate[]>([]);
  const setDate = useApp(($) => $.setDate);

  React.useEffect(() => {
    // declare the data fetching function
    const fetchData = async () => {
      await loadRates();
    };

    fetchData();
  }, [course_id]);

  // Load all rates for this course..  Do not worry about date, we will filter them later
  const loadRates = async () => {
    const { data, error } = await supabase.rpc("get_course_rates", {
      c_id: course_id,
    });

    if (data === null) {
      return;
    }
    setLoading(false);
    setRates(data);
  };

  const isWeekend = React.useMemo(() => {
    const day = dayjs(date).format("ddd");
    if (!day) return false;

    const key = day.toLowerCase() as keyof Days;

    if (weekend_days && weekend_days[key]) {
      return true;
    }
    return false;
  }, [date, weekend_days]);

  const active_warnings = React.useMemo(() => {
    return getActiveWarnings(date, warnings);
  }, [date, warnings]);

  const active_closure = React.useMemo(() => {
    let closure = getActiveClosures(date, closures);
    return closure.length > 0 ? closure[0] : null;
  }, [date, closures]);

  const active_holidays = React.useMemo(() => {
    return getActivePublicHolidays(date, public_holidays);
  }, [date, closures]);

  const getRatesForDate = React.useCallback(
    (date: string) => {
      let rates_obj = _.cloneDeep(rates);

      // remove all past rates
      rates_obj = rates_obj.filter((rate: any) => {
        // is today between the start and end date of the rate?
        if (dayjs(date).isAfter(dayjs(rate.end_date))) return false;

        return true;
      });

      let course_closures = getActiveClosures(date, closures);
      let results = getCourseRatesForDate(
        date,
        number_of_golfers,
        rates_obj,
        course_closures.length > 0 ? course_closures[0] : null,
        active_holidays.length > 0
      );

      return results.rates;
    },
    [rates, closures, number_of_golfers, active_holidays]
  );

  // get all the active rates based on date, number of golfers, and any warnings, closures, or holidays
  const active_rates = React.useMemo(() => {
    if (loading)
      return {
        message: "LOADING",
        rates: [],
        closures: false,
      };

    let rates_obj = _.cloneDeep(rates);

    let r = getCourseRatesForDate(
      date,
      number_of_golfers,
      rates_obj,
      active_closure,
      active_holidays.length > 0
    );

    return {
      message: r.message,
      rates: r.rates.sort((a: any, b: any) => {
        // sort by earliest_time then by sell_price
        let a_time = a.earliest_time;
        let b_time = b.earliest_time;

        if (a_time < b_time) return -1;
        if (a_time > b_time) return 1;

        let a_price = a.sell_price;
        let b_price = b.sell_price;

        if (a_price < b_price) return -1;
        if (a_price > b_price) return 1;
      }),
      closures: r.closure,
    };
  }, [
    rates,
    date,
    loading,
    number_of_golfers,
    active_holidays,
    active_closure,
    active_warnings,
  ]);

  React.useEffect(() => {
    if (onModeChange) onModeChange(active_rates);
  }, [active_rates]);

  const displayExistingBookingWarning = React.useMemo(() => {
    if (active_rates.rates.length === 0) return null;

    let bookings_today = cart?.filter((item: any) => {
      return item.golf.where_when_who.date === date;
    });
    if (bookings_today && bookings_today.length > 0) {
      return (
        <Alert
          spacing="tight"
          iconAlign="center"
          type="warning"
          title={`You already have ${
            bookings_today.length > 1 ? "rounds" : "a round"
          } at ${bookings_today
            .map((item: any) => {
              return `${item.golf.course.name.en}`;
            })
            .join(" and ")} in your cart for this day.`}
        />
      );
    }
  }, [date, cart, active_rates]);

  const displayPublicHolidaysWeekendWarning = React.useMemo(() => {
    if (active_rates.rates.length === 0) return null;
    if (active_holidays.length === 0) {
      // check if the date is a weekend
      if (isWeekend)
        return (
          <Alert
            spacing="tight"
            iconAlign="center"
            type="warning"
            title={"You've selected a weekend, so prices may be higher"}
          />
        );

      return null;
    }
    return active_holidays.map((holiday: PublicHoliday) => {
      return (
        <Alert
          spacing="tight"
          iconAlign="center"
          type="warning"
          title={"You've selected a public holiday, so prices may be higher"}
        />
      );
    });
  }, [active_holidays, isWeekend, active_rates]);

  const displayCourseWarnings = React.useMemo(() => {
    if (active_warnings.length === 0) return null;
    return active_warnings.map((warning: CourseWarning) => {
      return (
        <Alert
          spacing="tight"
          iconAlign="center"
          type="warning"
          title={mapWarningStateToText(warning, true)}
        />
      );
    });
  }, [active_warnings, active_rates]);

  const nearbyRates = React.useMemo(() => {
    // loop through each rate and get the start_date and end_date
    // across all the rates, find the earliest start_date and latest end_date
    let filtered_rates = rates.filter((rate: any) => {
      // is today between the start and end date of the rate?
      if (dayjs(date).isAfter(dayjs(rate.end_date))) return false;
      return true;
    });

    if (active_rates.message.includes("INVALID_NUMBER_OF_GOLFERS")) {
      // filter out rates that are not available for the number of golfers
      filtered_rates = filtered_rates.filter((rate: any) => {
        if (number_of_golfers >= rate.min_pax) {
          return true;
        }
        return false;
      });
    }

    let earliest_start_date = dayjs("9999-12-31");
    let latest_end_date = dayjs("0000-01-01");

    filtered_rates.forEach((rate: any) => {
      let start_date = dayjs(rate.start_date);
      let end_date = dayjs(rate.end_date);

      if (start_date.isBefore(earliest_start_date)) {
        earliest_start_date = start_date;
      }

      if (end_date.isAfter(latest_end_date)) {
        latest_end_date = end_date;
      }
    });

    let date_of_next_rate: any | null = null;
    // if there are no rates loop through each day until we find a rate
    let currentDate = dayjs(date);

    let found = false;
    while (!found) {
      currentDate = currentDate.add(1, "day");
      // if the current date is after the latest_end_date, then there are no rates
      // break out of the loop and return the message
      if (currentDate > latest_end_date) {
        date_of_next_rate = null;
        break;
      }

      let d = currentDate.format("YYYY-MM-DD");
      if (d) {
        let rates_for_date = getRatesForDate(d);
        if (rates_for_date.length > 0) {
          date_of_next_rate = currentDate;
          break;
        }
      }
    }

    // do the same as above but in reverse
    let date_of_previous_rate: Dayjs | null = null;
    currentDate = dayjs(date);
    // if the currentDate is after the latest_end_date set the currentDate to the latest_end_date
    if (currentDate > latest_end_date) {
      currentDate = latest_end_date;
    }
    found = false;
    while (!found) {
      currentDate = currentDate.subtract(1, "day");
      // if the current date is before the earliest_start_date, then there are no rates
      // break out of the loop and return the message
      if (currentDate < earliest_start_date || currentDate < dayjs()) {
        date_of_previous_rate = null;
        break;
      }
      let d = currentDate.format("YYYY-MM-DD");

      if (d) {
        let rates_for_date = getRatesForDate(d);
        if (rates_for_date.length > 0) {
          date_of_previous_rate = currentDate;
          break;
        }
      }
    }

    if (active_closure) {
      if (
        active_closure.reason === "CLOSED_FURTHER_NOTICE" &&
        active_closure.end_date
      ) {
        date_of_previous_rate = null;
      }
    }

    return (
      <>
        {!date_of_previous_rate && date_of_next_rate && (
          <>
            <Text size={15} leading={1.3} className="text-center">
              Please search for a different date. Earliest availability:{" "}
              {date_of_next_rate && (
                <span
                  className="font-semibold underline cursor-pointer"
                  onClick={() => {
                    if (date_of_next_rate)
                      setDate(date_of_next_rate.format("YYYY-MM-DD"));
                  }}
                >
                  {date_of_next_rate.format("ddd, DD MMM YYYY")}
                </span>
              )}
            </Text>
          </>
        )}

        {date_of_next_rate && date_of_previous_rate && (
          <>
            <Text size={15} leading={1.3} className="text-center">
              Please search for a different date. Nearest availability:{" "}
            </Text>

            <Text size={15} leading={1.3} className="text-center">
              {date_of_previous_rate && (
                <span
                  className="font-semibold underline cursor-pointer"
                  onClick={() => {
                    if (date_of_previous_rate)
                      setDate(date_of_previous_rate.format("YYYY-MM-DD"));
                  }}
                >
                  {date_of_previous_rate.format("ddd, DD MMM YYYY")}
                </span>
              )}
              <span> or </span>
              {date_of_next_rate && (
                <span
                  className="font-semibold underline cursor-pointer"
                  onClick={() => {
                    if (date_of_next_rate)
                      setDate(date_of_next_rate.format("YYYY-MM-DD"));
                  }}
                >
                  {date_of_next_rate.format("ddd, DD MMM YYYY")}
                </span>
              )}
            </Text>
          </>
        )}
      </>
    );
  }, [active_closure, number_of_golfers, active_rates]);

  let last_contract_enddate = getLastContractEndDate(contracts);

  React.useEffect(() => {
    switch (active_rates.message) {
      case "NO_RATES_EVER":
      case "NO_CURRENT_RATES":
      case "CLOSED_FURTHER_NOTICE_NO_END_DATE":
      case "CLOSED_FURTHER_NOTICE_WITH_END_DATE_WITHOUT_RATES":
      case "CLOSED_PERMANENTLY":
      case "DAYS_OF_OPERATION_CLOSURE": {
        setMode("enquiry");
        break;
      }
      default: {
        setMode("rates");
      }
    }
  }, [active_rates.message]);

  const displayRates = React.useMemo(() => {
    switch (active_rates.message) {
      case "NO_RATES_EVER":
      case "NO_CURRENT_RATES": {
        return (
          <RateEnquiry
            course_id={course_id}
            last_contract_date={last_contract_enddate}
            hide_request_quote={private_club}
            date={date}
          />
        );
      }
      case "CLOSED_FURTHER_NOTICE_NO_END_DATE": {
        return (
          <RateEnquiry
            course_id={course_id}
            hide_request_quote={private_club}
            last_contract_date={last_contract_enddate}
            date={date}
            closure={active_closure}
          />
        );
      }
      case "CLOSED_FURTHER_NOTICE_WITH_END_DATE_WITHOUT_RATES": {
        return (
          <RateEnquiry
            course_id={course_id}
            last_contract_date={last_contract_enddate}
            date={date}
            hide_request_quote={private_club}
            closure={active_closure}
          />
        );
      }
      case "CLOSED_FURTHER_NOTICE_WITH_END_DATE_WITH_RATES": {
        return (
          <RateWarning>
            <div className="flex flex-col gap-2 mb-4">
              <Text
                size={21}
                leading={1.3}
                weight="semibold"
                className="text-center"
              >
                Sorry, this course is closed until{" "}
                {dayjs(active_closure.end_date).format("ddd, DD MMM YYYY")}
              </Text>
              <Text size={15} leading={1.5} className="text-center">
                Please search for a different date.
              </Text>
              {nearbyRates}
            </div>
          </RateWarning>
        );
      }

      case "NO_CURRENT_RATES_HAS_FUTURE_RATES": {
        return (
          <RateWarning>
            <div className="flex flex-col gap-2 mb-4">
              <Text
                size={21}
                leading={1.3}
                weight="semibold"
                className="text-center"
              >
                We don’t have tee times available on this date
              </Text>
              <Text size={15} leading={1.5} className="text-center">
                Please search for a different date.
              </Text>
              {nearbyRates}
            </div>
          </RateWarning>
        );
      }
      case "CLOSED_PERMANENTLY": {
        return (
          <RateWarning>
            <div className="flex flex-col gap-2 mb-4">
              <Text
                size={21}
                leading={1.3}
                weight="semibold"
                className="text-center"
              >
                Sorry, this course is now permanently closed
              </Text>
              <Text size={15} leading={1.5} className="text-center">
                As far as we are aware, unfortunately this course has no plans
                to reopen.
              </Text>
            </div>
          </RateWarning>
        );
      }
      case "DAYS_OF_OPERATION_CLOSURE": {
        return (
          <RateWarning>
            <div className="flex flex-col gap-2 mb-4">
              <Text
                size={21}
                leading={1.3}
                weight="semibold"
                className="text-center"
              >
                This course is closed on {toDayOfWeek(date)}
              </Text>
              {nearbyRates}
            </div>
          </RateWarning>
        );
      }
      case "RATE_AVAILABLE_DAYS_CLOSURE": {
        return (
          <RateWarning>
            <div className="flex flex-col gap-2 mb-4">
              <Text
                size={21}
                leading={1.3}
                weight="semibold"
                className="text-center"
              >
                Sorry, we don’t have tee times available on this date
              </Text>
              {nearbyRates}
            </div>
          </RateWarning>
        );
      }
      case "CLOSURE_TODAY": {
        let closure = active_closure;
        return (
          <RateWarning>
            <div className="flex flex-col gap-2 mb-4">
              <Text
                size={21}
                leading={1.3}
                weight="semibold"
                className="text-center"
              >
                {mapClosureStateToText(closure)}
              </Text>
              {nearbyRates}
            </div>
          </RateWarning>
        );
      }
      case "INVALID_NUMBER_OF_GOLFERS_EVER": {
        let min_pax_today = rates
          .filter((rate: any) => {
            if (
              dayjs(date).isAfter(dayjs(rate.start_date)) &&
              dayjs(date).isBefore(dayjs(rate.end_date))
            ) {
              return true;
            }

            return false;
          })
          .filter((rate) => {
            // filter out the rates where the course days of operation do not include the day of the date
            const day = dayjs(date).format("ddd");
            if (!day) return false;

            const key = day.toLowerCase() as keyof Days;

            if (!rate.availability_days[key]) {
              return false;
            }
            return true;
          })
          .reduce((min: any, rate: any) => {
            if (rate.min_pax < min) return rate.min_pax;
            return min;
          }, 4);

        return (
          <RateWarning>
            <div className="flex flex-col gap-2 mb-4">
              <Text
                size={17}
                leading={1.3}
                weight="semibold"
                className="text-center"
              >
                Sorry, no tee times are available for{" "}
                <span className="font-bold">
                  {pluralize(number_of_golfers, "golfer", "golfers")}
                </span>{" "}
                on this day
              </Text>
              <Text size={15} leading={1.3} className="text-center">
                The minimum number of golfers is{" "}
                <span className="font-bold">{min_pax_today} players</span>.
                Please try increasing the number of players or choose a
                different course.
              </Text>
            </div>
          </RateWarning>
        );
      }
      case "NO_RATES_TODAY": {
        return (
          <RateWarning>
            <div className="flex flex-col gap-2 mb-4">
              <Text
                size={17}
                leading={1.3}
                weight="semibold"
                className="text-center"
              >
                We have no more availability for today
              </Text>
              {nearbyRates}
            </div>
          </RateWarning>
        );
      }
      case "NO_RATES_LATE_BOOKING": {
        return (
          <RateWarning>
            <div className="flex flex-col gap-2 mb-4">
              <Text
                size={17}
                leading={1.3}
                weight="semibold"
                className="text-center"
              >
                We can’t book a tee time online at such short notice
              </Text>
              <Text size={15} leading={1.3} className="text-center">
                If you’d like us try to secure you a tee time, please call us on{" "}
                <a
                  className="text-blue cursor-pointer underline"
                  href={`tel:${agent?.config?.email?.contact_phone}`}
                >
                  {agent?.config?.email?.contact_phone}
                </a>{" "}
                between 9am - 6pm (UTC+7) and we will do our best.
              </Text>
            </div>
          </RateWarning>
        );
      }
      case "INVALID_NUMBER_OF_GOLFERS_TODAY": {
        let min_pax_today = rates
          .filter((rate: any) => {
            if (
              dayjs(date).isAfter(dayjs(rate.start_date)) &&
              dayjs(date).isBefore(dayjs(rate.end_date))
            ) {
              return true;
            }

            return false;
          })
          .reduce((min: any, rate: any) => {
            if (rate.min_pax < min) return rate.min_pax;
            return min;
          }, 4);

        return (
          <RateWarning>
            <div className="flex flex-col gap-2 mb-4">
              <Text
                size={17}
                leading={1.3}
                weight="semibold"
                className="text-center"
              >
                Sorry, no tee times are available for{" "}
                <span className="font-bold">
                  {pluralize(number_of_golfers, "player", "players")}
                </span>{" "}
                on this day
              </Text>
              <Text size={15} leading={1.3} className="text-center">
                The minimum number of golfers is {min_pax_today} players.
              </Text>
              {nearbyRates}
            </div>
          </RateWarning>
        );
      }
      case "RATES_FOUND": {
        return (
          <>
            {active_rates.rates.map((rate: any, idx: any) => {
              return (
                <RateItem
                  key={idx}
                  rate={rate}
                  display_currency={display_currency}
                  number_of_golfers={number_of_golfers}
                  onSelected={onSelected}
                />
              );
            })}
          </>
        );
      }
    }
  }, [active_rates, date, display_currency]);

  if (loading) {
    return (
      <div className="min-h-[200px] inset-0  opacity-80 z-10 flex justify-center items-center">
        <svg
          aria-hidden="true"
          className="w-12 h-12 mr-2 text-gray-200 animate-spin dark:text-gray-600 fill-green"
          viewBox="0 0 100 101"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
            fill="#ddd"
          />
          <path
            d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
            fill="currentFill"
          />
        </svg>
      </div>
    );
  }

  return (
    <>
      {mode === "rates" && (
        <>
          {!active_closure && (
            <>
              {displayPublicHolidaysWeekendWarning}
              {displayCourseWarnings}
              {displayExistingBookingWarning}
            </>
          )}
        </>
      )}
      {displayRates}
    </>
  );
};

// const getCourseRatesForDate = (
//   date: string,
//   number_of_golfers: number,
//   rates: CourseRate[],
//   closure: CourseClosure,
//   public_holiday: boolean
// ): CourseRate_Response => {
//   const rates_obj = cloneObject(rates) as CourseRate[];

//   let is_closed_permanently = false;
//   let is_closed_until_further_notice = false;
//   let is_course_closed_today = false;
//   let closure_end_date = null;
//   let has_future_rates = false;
//   let has_current_or_future_rates = false;
//   let has_had_rates = false;
//   let has_current_rates = false;

//   let filtered_rates = cloneObject(rates) as CourseRate[];

//   // get the current closure for the date in the closures array.  if there is more than one get the first one
//   if (closure) {
//     if (closure.reason === "CLOSED_PERMANENTLY") {
//       is_closed_permanently = true;
//     }
//     if (closure.reason === "CLOSED_FURTHER_NOTICE") {
//       is_closed_until_further_notice = true;
//     }
//     if (closure.end_date) {
//       closure_end_date = closure.end_date;
//     }

//     // We have a closure so therefore the course is closed today
//     is_course_closed_today = true;
//   }

//   // Find out if there are any future rates
//   has_current_or_future_rates = rates.some((rate) => {
//     // is today between the start and end date of the rate?
//     if (dayjs(rate.start_date).isAfter(dayjs(date))) return true;
//     if (
//       dayjs(date).isSameOrAfter(dayjs(rate.start_date)) &&
//       dayjs(date).isSameOrBefore(dayjs(rate.end_date))
//     )
//       return true;

//     return false;
//   });

//   // Find out if there are any future rates
//   has_future_rates = rates.some((rate) => {
//     // is today between the start and end date of the rate?
//     if (dayjs(rate.start_date).isAfter(dayjs(date))) return true;
//     return false;
//   });

//   // Find out if there are any rates at all
//   if (rates.length > 0) {
//     has_had_rates = true;
//   }

//   // if the course is closed permanently, return CLOSED_PERMANENTLY
//   if (is_closed_permanently) {
//     return {
//       rates: [],
//       closure: false,
//       message: "CLOSED_PERMANENTLY",
//     };
//   }

//   // if the course is closed until further notice
//   if (is_closed_until_further_notice) {
//     const obj = {
//       rates: [],
//       closure: false,
//       message: "",
//     } as CourseRate_Response;

//     // if there is no end date, return CLOSED_FURTHER_NOTICE_NO_END_DATE
//     if (!closure_end_date) {
//       return {
//         ...obj,
//         message: "CLOSED_FURTHER_NOTICE_NO_END_DATE",
//       };
//     }

//     // if there are future rates, return CLOSED_FURTHER_NOTICE_WITH_END_DATE_WITH_RATES
//     if (has_current_or_future_rates) {
//       return {
//         ...obj,
//         message: "CLOSED_FURTHER_NOTICE_WITH_END_DATE_WITH_RATES",
//       };
//     }

//     // if there are no future rates, return CLOSED_FURTHER_NOTICE_WITH_END_DATE_WITHOUT_RATES
//     return {
//       ...obj,
//       message: "CLOSED_FURTHER_NOTICE_WITH_END_DATE_WITHOUT_RATES",
//     };
//   }

//   // if there are no rates, return NO_RATES_EVER
//   if (rates_obj.length === 0) {
//     return {
//       rates: [],
//       closure: false,
//       message: "NO_RATES_EVER",
//     };
//   }

//   // filter out the historical rates
//   filtered_rates = filtered_rates.filter((rate) => {
//     // is today between the start and end date of the rate?
//     if (dayjs(date).isAfter(dayjs(rate.end_date))) return false;
//     return true;
//   });

//   if (filtered_rates.length === 0) {
//     return {
//       rates: [],
//       closure: false,
//       message: "NO_CURRENT_RATES",
//     };
//   }

//   // does the date fall between the start and end date of a rate
//   has_current_rates = filtered_rates.some((rate) => {
//     // does the date fall between the start and end dates (inclusive) of the rate?
//     if (
//       dayjs(date).isSameOrAfter(dayjs(rate.start_date)) &&
//       dayjs(date).isSameOrBefore(dayjs(rate.end_date))
//     )
//       return true;
//     return false;
//   });

//   if (!has_current_rates && has_future_rates) {
//     return {
//       rates: [],
//       closure: false,
//       message: "NO_CURRENT_RATES_HAS_FUTURE_RATES",
//     };
//   }

//   // IS THE COURSE CLOSED FOR THE DAY (NOT BECAUSE OF STANDARD CLOSURE OR RATE UNAVAILABILITY ON THIS DAY)
//   if (is_course_closed_today) {
//     return {
//       rates: [],
//       closure: false,
//       message: "CLOSURE_TODAY",
//     };
//   }

//   //

//   // Check for DAYS_OF_OPERATION_CLOSURE
//   filtered_rates = filtered_rates.filter((rate) => {
//     // filter out the rates where the course days of operation do not include the day of the date
//     const day = dayjs(date).format("ddd");
//     if (!day) return false;

//     const key = day.toLowerCase() as keyof Days;

//     if (!rate.days_of_operation[key]) {
//       return false;
//     }
//     return true;
//   });

//   // If after this first level filter that there are no rates, return DAYS_OF_OPERATION_CLOSURE
//   if (filtered_rates.length === 0) {
//     return {
//       rates: [],
//       closure: false,
//       message: "DAYS_OF_OPERATION_CLOSURE",
//     };
//   }

//   // CHECK RATES ARE AVAILABLE FOR THE CURRENT DATE (NOT A CLOSURE)
//   filtered_rates = filtered_rates.filter((rate) => {
//     // filter out the rates where the course days of operation do not include the day of the date
//     const day = dayjs(date).format("ddd");
//     if (!day) return false;

//     const key = public_holiday ? "hol" : (day.toLowerCase() as keyof Days);

//     if (!rate.availability_days[key]) {
//       return false;
//     }
//     return true;
//   });

//   if (filtered_rates.length === 0) {
//     return {
//       rates: [],
//       closure: false,
//       message: "RATE_AVAILABLE_DAYS_CLOSURE",
//     };
//   }

//   const has_valid_number_of_golfers = filtered_rates.some((rate) => {
//     if (number_of_golfers >= rate.min_pax) {
//       return true;
//     }
//     return false;
//   });

//   // final filter...remove all reates for today if after 4pm..
//   if (dayjs(date).isSame(dayjs(), "day")) {
//     filtered_rates = filtered_rates.filter((rate: any) => {
//       // if the rate.latest_time is after 3 hours in the future
//       // (don't care about timezonese so we can assume last min bookigns are done in country)
//       if (dayjs().add(3, "hours").isAfter(dayjs(rate.latest_time, "HH:mm:ss")))
//         return false;

//       // if it is also TODAY you cannot book anythign who's end time is before 12pm
//       if (
//         dayjs(rate.latest_time, "HH:mm:ss").isBefore(
//           dayjs("12:00:00", "HH:mm:ss")
//         )
//       )
//         return false;
//       return true;
//     });

//     if (filtered_rates.length === 0) {
//       return {
//         rates: [],
//         closure: false,
//         message: "NO_RATES_TODAY",
//       };
//     }
//   }

//   if (dayjs(date).subtract(1, "day").isSame(dayjs(), "day")) {
//     filtered_rates = filtered_rates.filter((rate: any) => {
//       // get time today UTC+7
//       let now = dayjs().tz("Asia/Bangkok").format("HH:mm:ss");

//       let is_after_4pm = dayjs(now, "HH:mm:ss").isAfter(
//         dayjs("16:45:00", "HH:mm:ss")
//       );
//       if (is_after_4pm) {
//         // if the rate.ealiest_time is before 12pm hide it
//         if (
//           dayjs(rate.earliest_time, "HH:mm:ss").isBefore(
//             dayjs("12:00:00", "HH:mm:ss")
//           )
//         )
//           return false;
//       }

//       return true;
//     });

//     if (filtered_rates.length === 0) {
//       return {
//         rates: [],
//         closure: false,
//         message: "NO_RATES_LATE_BOOKING",
//       };
//     }
//   }

//   // ARE THERE ANY RATES WHERE THE NUMBER OF GOLFERS IS VALID
//   if (has_valid_number_of_golfers) {
//     const has_valid_number_of_golfers_for_date = filtered_rates.some((rate) => {
//       if (
//         number_of_golfers >= rate.min_pax &&
//         dayjs(date).isAfter(dayjs(rate.start_date)) &&
//         dayjs(date).isBefore(dayjs(rate.end_date))
//       ) {
//         return true;
//       }
//       return false;
//     });

//     const has_valid_number_of_golfers_in_future = filtered_rates.some(
//       (rate) => {
//         if (
//           number_of_golfers >= rate.min_pax &&
//           dayjs(date).isBefore(dayjs(rate.start_date))
//         ) {
//           return true;
//         }
//         return false;
//       }
//     );

//     if (
//       !has_valid_number_of_golfers_for_date &&
//       has_valid_number_of_golfers_in_future
//     ) {
//       return {
//         rates: [],
//         closure: false,
//         message: "INVALID_NUMBER_OF_GOLFERS_TODAY",
//       };
//     }

//     filtered_rates = filtered_rates.filter((rate) => {
//       if (number_of_golfers < rate.min_pax) {
//         return false;
//       }
//       return true;
//     });
//   } else {
//     return {
//       rates: [],
//       closure: false,
//       message: "INVALID_NUMBER_OF_GOLFERS_EVER",
//     };
//   }

//   // does the date fall between the start and end date of the rate?
//   filtered_rates = filtered_rates.filter((rate) => {
//     if (dayjs(date).isBefore(dayjs(rate.start_date))) return false;
//     if (dayjs(date).isAfter(dayjs(rate.end_date))) return false;

//     return true;
//   });

//   // Group the rates
//   const grouped_rates = filtered_rates.reduce((r: any, a: CourseRate) => {
//     r[
//       `${a.product_sku}_${a.product_type_name.en}_${a.course_hole_combination}_${a.min_pax}_${a.max_pax}_${a.multiples}`
//     ] = [
//       ...(r[
//         `${a.product_sku}_${a.product_type_name.en}_${a.course_hole_combination}_${a.min_pax}_${a.max_pax}_${a.multiples}`
//       ] || []),
//       a,
//     ];
//     return r;
//   }, {});

//   // Sort the rates
//   const sorted_rates = Object.keys(grouped_rates)
//     .map((key) => {
//       // group and sort them to get the cheapest rate
//       return grouped_rates[key].sort(
//         (a: any, b: any) => Number(a.sell_price) - Number(b.sell_price)
//       )[0];
//     })
//     .sort((a: any, b: any) => Number(a.sell_price) - Number(b.sell_price));

//   if (sorted_rates.length === 0) {
//     return {
//       rates: [],
//       closure: false,
//       message: "NO_RATES",
//     };
//   }

//   return {
//     rates: sorted_rates,
//     closure: false,
//     message: "RATES_FOUND",
//   };
// };
